import { useEffect, useState } from 'react';
import {
  listTravelPacksByOrder,
  packKindMeta,
  type SavedPack,
} from '@/lib/travelPack';

// ─────────────────────────────────────────────────────────────
// Détail « pack voyage » dans une commande marchand : dates,
// nuitées et prestations, pour distinguer ces réservations des
// ventes classiques.
// ─────────────────────────────────────────────────────────────

let cache: Record<number, SavedPack> | null = null;
let pending: Promise<Record<number, SavedPack>> | null = null;

function loadMap(): Promise<Record<number, SavedPack>> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = listTravelPacksByOrder()
      .then((map) => { cache = map; return map; })
      .catch(() => ({}) as Record<number, SavedPack>)
      .finally(() => { pending = null; });
  }
  return pending;
}

function frDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
}

function money(value: number, currency: string): string {
  return `${(Number(value) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`;
}

export default function PackOrderDetails({ orderId }: { orderId: number }) {
  const [pack, setPack] = useState<SavedPack | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    loadMap().then((map) => {
      if (!alive) return;
      setPack(map[Number(orderId)] || null);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, [orderId]);

  if (!loaded || !pack) return null;

  const items = pack.items.filter((item) => Number(item.orderId) === Number(orderId));
  const shown = items.length > 0 ? items : pack.items;

  return (
    <div className="mb-4 rounded-lg border border-accent-200/70 bg-accent-50/40 p-3.5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-[11px] font-bold">
          <i className="ri-luggage-cart-line"></i>Pack voyage
        </span>
        <span className="text-[11px] text-foreground-500">
          {frDate(pack.checkIn)} → {frDate(pack.checkOut)} · {pack.nights} nuit{pack.nights > 1 ? 's' : ''} · {pack.days} jour{pack.days > 1 ? 's' : ''} · {pack.guests} pers.
        </span>
      </div>

      <p className="text-sm font-semibold text-foreground-900">{pack.destination}</p>
      <p className="text-[11px] text-foreground-500 mt-0.5">
        Séjour composé par l'assistant IA — les autres prestations sont réservées chez leurs commerces respectifs.
      </p>

      <div className="mt-2.5 space-y-1.5">
        {shown.map((item, index) => {
          const meta = packKindMeta(item.kind);
          return (
            <div key={`${item.kind}-${index}`} className="flex items-center gap-2 text-[12px]">
              <i className={`${meta.icon} text-accent-700`}></i>
              <span className="min-w-0 flex-1 truncate text-foreground-800">
                {item.name}
                {item.merchantName ? <span className="text-foreground-400"> · {item.merchantName}</span> : null}
              </span>
              <span className="text-foreground-600 whitespace-nowrap">
                {item.price > 0 ? money(item.price, item.currency) : 'Sur devis'}
              </span>
            </div>
          );
        })}
      </div>

      {pack.program.length > 0 && (
        <details className="mt-2.5">
          <summary className="text-[11px] font-semibold text-accent-800 cursor-pointer whitespace-nowrap">
            Voir le programme jour par jour
          </summary>
          <div className="mt-2 space-y-1.5">
            {pack.program.map((day) => (
              <div key={day.day} className="text-[11px] text-foreground-600">
                <span className="font-semibold text-foreground-800">J{day.day} — {day.title}</span>{' '}
                <span className="text-foreground-400">({frDate(day.date)})</span>
                <span className="block text-foreground-500">{day.lines.join(' · ')}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}